/**
 * i18n foundation (LOT-I18N-CORE).
 *
 * Multilingual plumbing for the frontend. French (`fr`) is the default AND the
 * fallback language. English (`en`) is fully wired so `i18n.changeLanguage('en')`
 * works today, even though there is no language selector yet.
 *
 * Language DETECTION is intentionally DISABLED for now: the per-tenant / per-user
 * language is a later phase (a DB field + `changeLanguage()` at login). Do not add
 * i18next-browser-languagedetector here without coordinating that phase.
 *
 * Resources are BUNDLED (static JSON imports) — no async HTTP backend — so i18n is
 * ready synchronously and `react: { useSuspense: false }` avoids any <Suspense> need.
 *
 * One JSON file per DOMAIN namespace, under `locales/{fr,en}/`:
 *   - `common`  + `shell`  → filled by this lot (the reference model).
 *   - `contacts`, `campaigns`, `admin`, `stats`, `messaging` → scaffolds, filled by
 *     the parallel domain lots (LOT-I18N-{CONTACTS,ADMIN,STATS}). See ./GLOSSARY.md.
 *   - `contactImport` → the smart listing import (#208) : pages et composants de
 *     `src/components/contact-import/`. Scaffold vide posé par le socle, rempli
 *     par les lots d'interface (FR d'abord, EN en parité).
 *   - `telephony` → the v2 call scene (`src/telephony/**`), added by LOT-V2-PAGES.
 *     It ends the hardcoded French of the v2 beta pages. The v1 call pages keep
 *     their own strings: they are frozen and removed at cutover.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// French (default + fallback)
import commonFr from './locales/fr/common.json'
import shellFr from './locales/fr/shell.json'
import contactsFr from './locales/fr/contacts.json'
import campaignsFr from './locales/fr/campaigns.json'
import adminFr from './locales/fr/admin.json'
import statsFr from './locales/fr/stats.json'
import messagingFr from './locales/fr/messaging.json'
import logsFr from './locales/fr/logs.json'
import telephonyFr from './locales/fr/telephony.json'
import donationsFr from './locales/fr/donations.json'
import contactImportFr from './locales/fr/contactImport.json'

// English
import commonEn from './locales/en/common.json'
import shellEn from './locales/en/shell.json'
import contactsEn from './locales/en/contacts.json'
import campaignsEn from './locales/en/campaigns.json'
import adminEn from './locales/en/admin.json'
import statsEn from './locales/en/stats.json'
import messagingEn from './locales/en/messaging.json'
import logsEn from './locales/en/logs.json'
import telephonyEn from './locales/en/telephony.json'
import donationsEn from './locales/en/donations.json'
import contactImportEn from './locales/en/contactImport.json'

/** Namespace loaded when a key has no `ns:` prefix. */
export const defaultNS = 'common' as const

/** All domain namespaces. A new domain lot appends its name here. */
export const namespaces = [
  'common',
  'shell',
  'contacts',
  'campaigns',
  'admin',
  'stats',
  'messaging',
  'logs',
  'telephony',
  'donations',
  'contactImport',
] as const

/** Languages the app can switch to. */
export const supportedLngs = ['fr', 'en'] as const

export const resources = {
  fr: {
    common: commonFr,
    shell: shellFr,
    contacts: contactsFr,
    campaigns: campaignsFr,
    admin: adminFr,
    stats: statsFr,
    messaging: messagingFr,
    logs: logsFr,
    telephony: telephonyFr,
    donations: donationsFr,
    contactImport: contactImportFr,
  },
  en: {
    common: commonEn,
    shell: shellEn,
    contacts: contactsEn,
    campaigns: campaignsEn,
    admin: adminEn,
    stats: statsEn,
    messaging: messagingEn,
    logs: logsEn,
    telephony: telephonyEn,
    donations: donationsEn,
    contactImport: contactImportEn,
  },
} as const

i18n.use(initReactI18next).init({
  resources,
  lng: 'fr',
  fallbackLng: 'fr',
  supportedLngs: [...supportedLngs],
  ns: [...namespaces],
  defaultNS,
  interpolation: {
    escapeValue: false, // React already escapes values against XSS.
  },
  react: {
    useSuspense: false, // Resources are bundled — no async load, no Suspense boundary.
  },
  returnNull: false,
})

export default i18n
